import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { DEFAULT_INTERRUPTSOURCES, DocumentInterruptSource, Idle, InterruptSource } from "@ng-idle/core";
import { SessionSettings, SettingsSectionsName, SystemConfigurationService } from "@main/system-configuration";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject, firstValueFrom, switchMap, takeUntil, tap, of, map } from "rxjs";
import { OrganizationService } from "@main/companies";
import { SelfRegisterInfoService } from "@project/self-registration/self-register-info.service";

@Component({
    selector: "self-register-player",
    template: `
        <p-dialog
            [(visible)]="showBanner"
            class="custom-dialog"
            [draggable]="false"
            position="center"
            modal="true"
            appendTo="body"
            [closeOnEscape]="false"
            [baseZIndex]="10000"
            header="Timeout Warning"
            [style]="{ height: '40px', display: 'initial' }">
            <span class="idle-banner-remaining-time" rte="Xe">
                Do you need some more time to complete your registration as a new patient? Please click Continue.<br /><br />
                Otherwise you will be redirected to the home page after {{ countDown }} seconds
            </span>

            <p-footer rte="VP">
                <button allowOffline pButton type="button" (click)="showBanner = false" label="Continue" rte="VQ">
                    <i class="material-icons" rte="VR">check</i>
                </button>
            </p-footer>
        </p-dialog>
        <div class="form-container" [ngClass]="{ 'org-self-registration': orgSelfRegistration }">
            <create-self-register-player-form
                [url]="previousURL"
                [draftAppointmentKey]="draftAppointmentKey"
                [formStyle]="{ 'background-color': 'white', padding: '30px', 'margin-bottom': '30px' }">
            </create-self-register-player-form>
        </div>
    `,
    styleUrls: ["self-register-player.component.scss"],
    providers: [SelfRegisterInfoService]
})
export class SelfRegisterPlayerComponent implements OnInit, OnDestroy {
    protected readonly slfRegisterInfoService = inject(SelfRegisterInfoService);

    private destroyed$ = new Subject<void>();
    orgSelfRegistration = false;
    previousURL = "app";
    draftAppointmentKey: string = null;

    public static readonly IdleKey = "mipro.self-register-patient-idle";

    showBanner: boolean;
    countDown: number;

    constructor(
        private idle: Idle,
        private router: Router,
        private sysConfig: SystemConfigurationService,
        private route: ActivatedRoute,
        private organizationService: OrganizationService
    ) {
        idle.setIdleName(SelfRegisterPlayerComponent.IdleKey);
    }

    async ngOnInit() {
        let organizationUrl: string;
        const result = await firstValueFrom(
            this.route.params.pipe(
                tap(params => {
                    organizationUrl = params.organizationurl;
                }),
                switchMap(params => params.organizationurl ? this.organizationService.isSelfRegistrationEnabled(params.organizationurl) : of(false))
            )
        );
        const queryParams = (await firstValueFrom(
                this.route.queryParams.pipe(map(p => ({ ...p })))
            ))

        if (organizationUrl) {
            if(!result) {
                this.router.navigate(["app"]);
                return;
            } else {
                if(queryParams.returnUrl) {
                    this.previousURL = queryParams.returnUrl;
                } else {
                    this.orgSelfRegistration = true;
                    let prevUrl = `selfregistration/clinic?clinic=${organizationUrl}`;
                    const externalpatientid = await firstValueFrom(this.slfRegisterInfoService.externalPatientId$);
                    if (externalpatientid) {
                        prevUrl = `${prevUrl}&externalpatientid=${externalpatientid}`;
                    }

                    this.previousURL = prevUrl
                }

                if(queryParams.draftAppointmentKey) {
                    this.draftAppointmentKey = queryParams.draftAppointmentKey;
                }
            }
        }


        const section = this.sysConfig.getSettingsSection<SessionSettings>(SettingsSectionsName.UserSessionSettings);
        if (!section) {
            return;
        }

        this.configureIdle(section);
        this.configureListeners();
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.idle?.stop();
    }

    private configureListeners(): void {
        this.idle.onIdleStart.pipe(takeUntil(this.destroyed$)).subscribe(() => (this.showBanner = true));
        this.idle.onTimeoutWarning.pipe(takeUntil(this.destroyed$)).subscribe(countdown => (this.countDown = countdown));
        this.idle.onInterrupt.pipe(takeUntil(this.destroyed$)).subscribe(() => (this.showBanner = false));
        this.idle.onTimeout.pipe(takeUntil(this.destroyed$)).subscribe(() => this.router.navigateByUrl(this.previousURL));
    }

    private configureIdle(sessionSettingsSection: SessionSettings): void {
        this.idle.clearInterrupts();
        this.idle.stop();

        const idleTime = sessionSettingsSection.idleTimeForSelfRegisterPage;
        if (sessionSettingsSection.idleTimeEnabled && idleTime) {
            this.idle.setIdle(idleTime * 60);
            this.idle.setTimeout(30);
            const interrupts: InterruptSource[] = [...DEFAULT_INTERRUPTSOURCES, new KeyboardInterruptSource()];
            this.idle.setInterrupts(interrupts);
            this.idle.watch();
        }
    }
}

class KeyboardInterruptSource extends DocumentInterruptSource {
    constructor() {
        super("keydown");
    }
}
